import {environment} from "../../../environments/environment";
import {isNonNullObject, toIsoDate} from "../classes/Utils";
import {CommonService} from "../../shared/services/common.service";

export enum LogLevel {
  ALL = 100,
  DEBUG = 200,
  INFO = 300,
  PROD = 400,
}

export enum SelectionType {
  SINGLE = "SINGLE",
  MULTIPLE = "MULTIPLE",
}

export enum State {
  ALL = null,
  EXPIRED = 3,
  ACTIVE = 1,
  INACTIVE = 0,
}

export enum Sort {
  ASC = "ASC",
  DESC = "DESC"
}

export enum Multiselect {
  EMAIL = "email",
  ROLE_FILTER = "roleFilter",
  ROLE_CREATION = "roleCreation",
  COMPANY = "company",
  SUPPLIER = "supplier",
  DISCOUT = "discount",
  UM = "um",
  USER = "user",
  RESELLER = "reseller",
  RESELLER_OWN = "resellerOwn",
  HEADQUARTER = "headquarter",
  PROJECT_TYPE = "projectType",
  PROJECT_CATEGORY = "projectCategory",
  WORKFLOW = "workflow",
  CUSTOMER_TYPE = "customerType",
  PROJECT_PHASES = "projectPhases",
  PROJECT_CHECKLISTS = "projectChecklists",
  PROJECT_ACTIVITIES = "projectActivities",
  STATE = "state",
  DOCUMENT_TYPE = "documentType",
  REMINDER_ASSOCIABLE_USERS = "projectReminderAssociableUsers",
  PAYMENT = "payment",
  PAYMENT_CHANNEL = "paymentChannel",
  CREATION_SITE = "creationSite",
  ACCOUNT_TYPE = "accountType",
  SEX = "sex",
  PRODUCT_TYPE = "productType",
  PRODUCT_CATEGORY = "productCategory",
  PRODUCT_SUB_CATEGORY = "productSubCategory",
  PRODUCT_CATALOG = "productCatalog",
  PRODUCT_SIZE = "productSize",
  ORDER_CATALOG = "orderCatalog",
  ORDER_JOB = "orderJob",
  ORDER_JOB_STATUS = "jobStatus",
  ORDER_JOB_TYPE = "jobType"
}

export interface MultiselectSettings {
  singleSelection: boolean;
  text: string;
  searchPlaceholderText: string;
  selectAllText?: string;
  unSelectAllText?: string;
  enableSearchFilter: boolean;
  classes: string;
  badgeShowLimit?: number;
  disabled?: boolean;
}

// Value key MultiselectItems like enum
export interface MultiselectItems {
  currency?: MultiselectItem[];
  email?: MultiselectItem[];
  roleFilter?: MultiselectItem[];
  roleCreation?: MultiselectItem[];
  company?: MultiselectItem[];
  user?: MultiselectItem[];
  reseller?: MultiselectItem[];
  resellerOwn?: MultiselectItem[];
  headquarter?: MultiselectItem[];
  projectType?: MultiselectItem[];
  projectCategory?: MultiselectItem[];
  workflow?: MultiselectItem[];
  projectActivityState?: MultiselectItem[];
  outcome?: MultiselectItem[];
  customerType?: MultiselectItem[];
  suppliers?: MultiselectItem[];
  discounts?: MultiselectItem[];
  projectPhases?: MultiselectItem[];
  projectChecklists?: MultiselectItem[];
  projectActivities?: MultiselectItem[];
  state?: MultiselectItem[];
  documentType?: MultiselectItem[];
  projectReminderAssociableUsers?: MultiselectItem[];
  jobId?: MultiselectItem[];
  depositType?: MultiselectItem[];
  payment?: MultiselectItem[];
  paymentChannel?: MultiselectItem[];
  paymentStatus?: MultiselectItem[];
  creationSite?: MultiselectItem[];
  accountType?: MultiselectItem[];
  sex?: MultiselectItem[];
  productType?: MultiselectItem[];
  productCategory?: MultiselectItem[];
  productSubCategory?: MultiselectItem[];
  productCatalog?: MultiselectItem[];
  productSize?: MultiselectItem[];
  orderCatalog?: MultiselectItem[];
  orderJob?: MultiselectItem[];
  jobStatus?: MultiselectItem[];
  jobType?: MultiselectItem[];
  um?: MultiselectItem[];
}

export interface MultiselectItem {
  id: any;
  itemName: string;
  additionalValues?: any;
}

export interface SelectItem {
  id: number | string;
  name: string;
  state?: number;
  data?: any;
}

export interface LoggedUserInfo {
  token: string;
  name: string;
  surname: string;
  enabled: boolean;
  role: string;
}

export interface Email {
  id?: string
  to: string[]
  bcc: string[] | null
  ccn: string[] | null
  subject: string
  body: string
  attachments: File[]

  children?: Email[]
}

export interface RequestBody {
  paging: boolean;
  page: number;
  rows: number;
  summary?: boolean;
  orderBy?: string;
  direction?: string;
  filters: EntityEnum;
  filter?: string;
}

export function toSelectItem(record: any, inputFieldKey: string, outputFieldKey: string) {
  const res = {
    id: record.id,
    disabled: record.state ? record.state != 1 : true,
    data: record
  }
  res[outputFieldKey] = record[inputFieldKey]
  return res;
}

export function autocompleteRequest() : any {
  return {
    paging: false,
    page: 0,
    rows: 200,
    filters: null,
    filter: ''
  }
}

export interface EnumValue {
  id: number
  name: string
  label: string
}

export interface NamedDto {
  id: number
  name: string
}

export abstract class ListRequestBody implements RequestBody {
  paging: boolean = true;
  page: number = 0;
  rows: number = environment.rows;
  summary?: boolean = true;
  orderBy?: string = 'name';
  direction?: string = 'ASC';
  filters: EntityEnum;
  excluded?: string = null;

  constructor(filters: EntityEnum) {
    this.filters = filters;
  }

  toRequest() : ListRequestBody {
    return JSON.parse(JSON.stringify(this))
  }

  normalizeCreationData(pt: any, filter: string) {
    pt.company = isNonNullObject(pt.company) ? pt.company : []
    pt.creationDateStart = toIsoDate(this.filters[filter].creationDateStart)
    pt.creationDateEnd = toIsoDate(this.filters[filter].creationDateEnd)
    pt.updateDateStart = toIsoDate(this.filters[filter].updateDateStart)
    pt.updateDateEnd = toIsoDate(this.filters[filter].updateDateEnd)

  }

  abstract reset()
}

export interface EntityEnum {
  USER?: any;
  ROLE?: any;
  HEADQUARTER?: any;
  PROJECT_CATEGORY?: any;
  PROJECT_TYPE?: any;
  WORKFLOW?: any;
  PROJECT_VIEW?: any;
  DOC_ACTIVITY?: any;
  CUSTOMER?: any;
  PHASE?: any;
  CHECKLIST?: any;
  ACTIVITY?: any;
  ACTIVITY_FILE?: any;
  REMINDER?: any;
  SUBCONTRACTOR?: any;
  PROJECT_SAFETY_FILE?: any;
  DOC?: any;
  COMPANY?: any;
  DEPOSIT?: any;
  PAYMENT?: any;
  DISCOUNT?: any;
  VATRATE?: any;
  ACCOUNT?: any;
  PRODUCT?: any;
  PRODUCT_CATEGORY?: any;
  PRODUCT_SUB_CATEGORY?: any;
  PRODUCT_IMAGE?: any;
  PRODUCT_TYPE?: any;
  CATALOG_IMPORT?: any;
  QUOTE?: any;
  CUST_ORDER?: any;
  ADVANCE_PAYMENT?: any;
  CUST_INVOICE?: any;
  BOM?: any;
  SAL_PROJECT_SUMMARY?: any;
  SAL?: any;
}

export enum PossibleActionsType {
  EDIT = "EDIT",
  VIEW = "VIEW",
  ADD = "ADD",
  DELETE = "DELETE",
  DUPLICIATE = "DUPLICIATE",
  TOGGLE_STATE = "TOGGLE_STATE",
}
export interface PossibleActions {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  delete?: boolean;
  duplicate?: boolean;
  disassociate?: boolean;
  reminderCanEditOutcome?: boolean;
  toggle?: boolean;
}

export interface CellConfiguration {
  title?: string;
  name?: string;
  key?: string;
  onChip?: boolean;
}

export interface SweetAlertMsg {
  loadingText?: string;
  creationText?: string;
  updatingText?: string;
  errorText?: string;
  firstError?: string;
  secondError?: string;
  warning?: string;
}

export interface ToastMessage {
  elementList: any;
  multiselect: any;
  toggle_active_ok: string | null;
  toggle_active_er: string | null;
  duplicate_ok: string | null;
  duplicate_er: string | null;
  delete_er: string | null;
  delete_ok: string | null;
  invite_ok: string | null;
  invite_er: string | null;
}

export interface CreationMetadata {
  creationDatetime: string;
  updateDatetime: string;
  userLastUpdate: number;
}


// server enums handling
export enum Enums {
  bomType = 'bomType',
  bomEntryType = 'bomEntryType',
  bomEntryState = 'bomEntryState',
}

export enum BomType {
  item = 'item',
  manufacturing = 'manufacturing'
}

export enum BomEntryType {
  internal = 'internal',
  extra = 'extra'
}

export function enumEntry(enumValue: any, enumName: Enums, commonService: CommonService) {
  return commonService.bootstrapData.enums[enumName].find(t => enumValue == t.name)
}

